import { HOVER_ENTER_EVENT } from '@simlab/simlab-facility-management/common';
import {
  ChildConfiguration,
  ClickEventEmitter,
  ComponentConfiguration,
  HoverEventEmitter,
  ICustomComponent,
  getBoundingRadius,
} from '@simlab/simlab-facility-management/scene-object';
import { TransformConverter } from '@simlab/transform';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import type THREE from 'three';
import { type Camera, type Scene } from 'three';
import { moveThreejsPositionByOffset } from '../common/move-by-offset';
import { TagComponent } from '../components/tag-component';
/**

 * @param camera - (Camera): The camera object used for rendering the scene.
 * @param scene - (Scene): The scene object where the tag components will be
 * @param offset -  (TransformConverter): An instance of the TransformConverter class used for calculating position offsets.
 * @returns - (ICustomComponent<TagComponent>): An object with methods for managing tag components.
 * @description returns factory that  creates and manages tag components in a 3D scene. It takes a camera, a scene, and an instance of the TransformConverter class as inputs. The function returns an object with methods for adding, updating, and deleting tag components, as well as managing the selected tag component.
  *@example
  *import { tagComponentFactory } from './tagComponentFactory';
  *
  *const camera = new PerspectiveCamera();
  *const scene = new Scene();
  *const offset = new TransformConverter();
  *
  *const tagComponentManager = tagComponentFactory(camera, scene, offset);
  *
  *tagComponentManager.addComponent$({
  *  id: 'tag1',
  *  position: { x: 0, y: 0, z: 0 },
  *  normal: { x: 0, y: 1, z: 0 },
  *  objects: [],
  *}).subscribe((component) => {
  *  console.log('Tag component added:', component);
  *});
  *
  *tagComponentManager.updateNote({
  *  id: 'tag1',
  *  position: { x: 1, y: 0, z: 0 },
  *  normal: { x: 0, y: 1, z: 0 },
  *  stemHeight: 1,
  *});
  *
  *tagComponentManager.deleteNote('tag1');
 */
export class TagComponentFactory implements ICustomComponent<TagComponent> {
  private readonly _components: Map<string, TagComponent> = new Map();
  private readonly _componentClicked: Subject<ClickEventEmitter> =
    new Subject<ClickEventEmitter>();
  private readonly _componentHovered: BehaviorSubject<
    HoverEventEmitter | undefined
  > = new BehaviorSubject<HoverEventEmitter | undefined>(undefined);
  private readonly _selectedTag: BehaviorSubject<string | undefined> =
    new BehaviorSubject<string | undefined>(undefined);
  readonly selectedNote$ = this._selectedTag.asObservable();
  readonly componentClicked$ = this._componentClicked.asObservable();
  /** Only one component is hovered at a time; `undefined` clears the tooltip. */
  readonly componentHovered$ = this._componentHovered.asObservable();

  constructor(
    private readonly _camera: Camera,
    private readonly _scene: Scene,
    private readonly _offset: TransformConverter,
    private readonly _THREE: typeof THREE
  ) {}

  private _addCollider = (tagComponent: TagComponent) => {
    tagComponent.onClick = (event, component) => {
      this._componentClicked.next({
        id: component.inputs.id,
        userData: component.inputs.userData,
      });
    };
    tagComponent.onHover = (event, component) => {
      const id = component.inputs.id;
      if (event.type === HOVER_ENTER_EVENT) {
        this._componentHovered.next(this._hoverEvent(component));
      } else if (this._componentHovered.getValue()?.id === id) {
        this._componentHovered.next(undefined);
      }
    };
  };
  private _hoverEvent(component: TagComponent): HoverEventEmitter {
    return {
      id: component.inputs.id,
      userData: component.inputs.userData,
      point: component.position.clone(),
      radius: getBoundingRadius(component._group),
      tooltip: component.inputs.tooltip,
    };
  }
  getHoverEvent = (id: string): HoverEventEmitter | undefined => {
    const component = this._components.get(id);
    return component ? this._hoverEvent(component) : undefined;
  };
  private _canAddComponent = (id: string) => {
    if (this._components.get(id))
      throw Error(`Tag with id ${id} already exist`);
  };
  private _getComponent = (id: string): TagComponent => {
    const component = this._components.get(id);
    if (!component) throw Error(`Tag with id ${id} does not exist in scene`);
    return component;
  };
  /**
   * Selection is driven by the host (e.g. the element currently open on a side sheet), not by
   * clicks. The id is kept even when its component is missing or gets re-created, and the
   * `selected` state is applied as soon as the component exists.
   */
  set selectedNote(noteId: string | undefined) {
    const previous = this._selectedTag.getValue();
    if (previous && previous !== noteId) this._setState(previous, 'default');
    if (noteId) this._setState(noteId, 'selected');
    this._selectedTag.next(noteId);
  }

  private _setState(noteId: string, state: TagComponent['state']) {
    const component = this._components.get(noteId);
    if (component) component.state = state;
  }
  get selectedNote(): string | undefined {
    return this._selectedTag.getValue();
  }

  addComponent$ = (
    component: ComponentConfiguration
  ): Observable<TagComponent> => {
    this._canAddComponent(component.id);
    const _tagComponent = new TagComponent(
      this._camera,
      this._scene,
      component,
      this._THREE
    );

    component.objects.forEach((child: ChildConfiguration) => {
      child.init(this._THREE);
      _tagComponent.createChildFromInstance(child.instance);
    });
    const anySprite = component.objects.some(
      (obj) => obj.instance.object3D.type === 'Sprite'
    );

    // // To think how to scale BOX
    // if (anySprite) {
    //   const dummyBox = new Mesh(
    //     new BoxGeometry(0.9, 0.9, 0.9),
    //     new MeshBasicMaterial({
    //       visible: !!component.showhelperBox,
    //     })
    //   );
    //   dummyBox.position.copy(new Vector3(0, 0, 0));
    //   // dummyBox.scale.copy(component.scale);
    //   _tagComponent._group.add(dummyBox);
    // }

    this._components.set(component.id, _tagComponent);
    if (this._selectedTag.getValue() === component.id)
      _tagComponent.state = 'selected';
    _tagComponent.collision = component.isCollider ?? true;
    _tagComponent.drawStem();
    if (component.showhelperBox) {
      _tagComponent.addBoxHelper();
    }
    this._addCollider(_tagComponent);
    return of(_tagComponent);
  };
  addComponentWithOffset$ = (
    component: ComponentConfiguration
  ): Observable<TagComponent> => {
    const movedComponent = moveThreejsPositionByOffset(component, this._offset);
    return this.addComponent$({
      ...component,
      ...movedComponent,
    });
  };
  updateNote$ = (
    component: Pick<
      ComponentConfiguration,
      'position' | 'normal' | 'id' | 'stemHeight'
    >
  ): Observable<void> => {
    return of(this.updateNote(component));
  };
  updateNote = (
    component: Pick<
      ComponentConfiguration,
      'position' | 'normal' | 'id' | 'stemHeight'
    >
  ): void => {
    const _component = this._getComponent(component.id);
    _component.inputs.normal = component.normal;
    _component.inputs.stemHeight = component.stemHeight;
    _component.position = component.position;
  };
  updatePositionWithOffset = (
    component: Pick<
      ComponentConfiguration,
      'position' | 'normal' | 'id' | 'stemHeight' | 'rotation' | 'scale'
    >
  ): void => {
    this.updateNote({
      ...component,
      ...moveThreejsPositionByOffset(component, this._offset),
    });
  };
  updatePositionWithOffset$ = (
    component: Pick<
      ComponentConfiguration,
      'position' | 'normal' | 'id' | 'stemHeight' | 'rotation' | 'scale'
    >
  ): Observable<void> => {
    return of(this.updatePositionWithOffset(component));
  };
  deleteNote = (noteId: string): string => {
    if (this._components.get(noteId)) {
      const component = this._getComponent(noteId);
      // the selection is the host's intent: it survives a delete / re-add of the component
      if (this._componentHovered.getValue()?.id === noteId)
        this._componentHovered.next(undefined);
      component.destroy();
      this._components.delete(noteId);
      return noteId;
    }
    return '';
  };
  deleteNote$ = (noteId: string): Observable<string> => {
    return of(this.deleteNote(noteId));
  };
  clearAllNotes = (): void => {
    this._components.forEach((_: TagComponent, id: string) => {
      this.deleteNote(id);
    });
  };

  clearAllNotes$ = (): Observable<void> => {
    return of(this.clearAllNotes());
  };
}
